import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Typography,
  Box,
  Chip,
  Divider,
  Link,
  Grid,
} from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import ImageIcon from '@material-ui/icons/Image';
import PdfIcon from '../../../../assets/images/event/pdf-icon.png';
import DownloadIcon from '../../../../assets/images/event/download-icon.png';
import LinkIcon from '../../../../assets/images/event/link-icon.png';
import ExtLinkIcon from '../../../../assets/images/event/ext-link-icon.png';

const ReportList = ({
  reports = [], classes = {}, statusColorMap = {}, md = null,
}) => {
  if (!reports || reports.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        ไม่พบข้อมูลเหตุการณ์ย่อย
      </Typography>
    );
  }

  const getFileType = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();

    if (ext === 'pdf') return 'pdf';

    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return 'image';
    }

    return 'other';
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        เหตุการณ์ย่อย ทั้งหมด
      </Typography>

      {reports.map((rp) => {
        const html = md?.render(rp.description || '');

        return (
          <Box key={rp.id} className={classes.reportCard} boxShadow={2}>
            {/* Header */}
            <Box display="flex">
              <Typography variant="h6" className={classes.eventTitle}>
                #
                {rp.id}
                {' '}
                {rp.report_name}
              </Typography>
            </Box>

            {/* Meta */}
            <Box display="flex" alignItems="center" className={classes.metaRow}>
              <CalendarTodayIcon fontSize="small" />
              <Typography variant="caption" display="inline" className={classes.eventDate}>
                {rp.report_date
                  ? moment(rp.report_date).format('D MMM YYYY')
                  : '-'}
              </Typography>
              <Typography
                variant="caption"
                display="inline"
                className={classes.statusDot}
                style={{ color: statusColorMap?.[rp.status] || '#6b7280' }}
              >
                {rp.status_name?.name || '-'}
              </Typography>
            </Box>
            <Divider style={{ margin: '16px 0' }} />
            {/* Description (Markdown → HTML) */}
            {html && (
              <>
                <Box
                  mt={2}
                  className={classes.markdown}
                  dangerouslySetInnerHTML={{ __html: html }}
                />
                <br />
              </>
            )}

            <Divider style={{ margin: '16px 0' }} />

            {((rp.tags || []).length > 0 || (rp.urls || []).length > 0) && (
              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    {(rp.areas || []).length > 0 && (
                      <>
                        <Typography variant="subtitle2" gutterBottom>
                          พื้นที่ที่ได้รับผลกระทบ
                        </Typography>

                        <ul style={{
                          marginTop: 6, marginLeft: 10, paddingLeft: 18, fontSize: '0.8rem',
                        }}
                        >
                          {rp.areas.map((a) => (
                            <li key={a.id}>{a.label}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box display="flex">
                      <Typography variant="subtitle2" className={classes.tagLabel} gutterBottom>
                        แท็ก
                      </Typography>
                      {(rp.tags || []).length > 0 && (
                        <Box ml={2}>
                          {rp.tags.map((tag) => (
                            <Chip
                              key={tag.id}
                              label={tag.name}
                              size="small"
                              className={classes.tagChip}
                              style={{
                                background: '#E0E0E0',
                                color: '#000',
                                marginRight: 6,
                                marginBottom: 6,
                                padding: '2px 15px',
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" className={classes.tagLabel} gutterBottom>
                      ไฟล์แนบ
                    </Typography>
                    {/* ===== PDF ก่อน ===== */}
                    {(rp.attachments || [])
                      .filter((file) => getFileType(file.file_name) === 'pdf')
                      .map((file) => (
                        <Link
                          key={file.id}
                          href={file.full_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.attachmentItem}
                          underline="none"
                        >
                          <Box className={classes.attachmentLeft}>
                            <img
                              src={PdfIcon}
                              alt="PDF"
                              style={{ width: 20, marginLeft: 3 }}
                            />
                            <Typography className={classes.attachmentName}>
                              {file.file_name}
                            </Typography>
                          </Box>

                          <img src={DownloadIcon} alt="Download" style={{ width: 20 }} />
                        </Link>
                      ))}

                    {/* ===== IMAGE ต่อ ===== */}
                    {(rp.attachments || [])
                      .filter((file) => getFileType(file.file_name) === 'image')
                      .map((file) => (
                        <Link
                          key={file.id}
                          href={file.full_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.attachmentItem}
                          underline="none"
                        >
                          <Box className={classes.attachmentLeft}>
                            <ImageIcon className={classes.iconImage} />
                            <Typography className={classes.attachmentName}>
                              {file.file_name}
                            </Typography>
                          </Box>

                          <img src={DownloadIcon} alt="Download" style={{ width: 20 }} />
                        </Link>
                      ))}

                    {(rp.urls || []).map((u) => (
                      <Link
                        key={u.id}
                        href={u.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.attachmentItem}
                      >
                        {/* LEFT */}
                        <Box className={classes.attachmentLeft}>
                          <img src={LinkIcon} alt="Link" style={{ width: 20 }} />
                          <Typography className={classes.attachmentName}>
                            {u.title || u.url}
                          </Typography>
                        </Box>

                        {/* RIGHT */}
                        <img src={ExtLinkIcon} alt="External Link" style={{ width: 20 }} />
                      </Link>
                    ))}
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        );
      })}
    </>
  );
};

ReportList.propTypes = {
  reports: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      report_name: PropTypes.string,
      report_date: PropTypes.string,
      description: PropTypes.string,
      status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status_name: PropTypes.shape({
        name: PropTypes.string,
      }),
      tags: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          name: PropTypes.string,
        }),
      ),
      attachments: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          file_name: PropTypes.string.isRequired,
          full_url: PropTypes.string.isRequired,
        }),
      ),
      urls: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          title: PropTypes.string,
          url: PropTypes.string.isRequired,
        }),
      ),
    }),
  ),

  classes: PropTypes.shape({
    reportCard: PropTypes.string,
    eventTitle: PropTypes.string,
    metaRow: PropTypes.string,
    eventDate: PropTypes.string,
    statusDot: PropTypes.string,
    markdown: PropTypes.string,
    tagLabel: PropTypes.string,
    tagChip: PropTypes.string,
    attachmentItem: PropTypes.string,
    attachmentLeft: PropTypes.string,
    attachmentName: PropTypes.string,
    iconImage: PropTypes.string,
  }),

  statusColorMap: PropTypes.shape({
    public: PropTypes.string,
    private: PropTypes.string,
    draft: PropTypes.string,
  }),

  md: PropTypes.shape({
    render: PropTypes.func.isRequired,
  }),
};
ReportList.defaultProps = {
  reports: [],
  classes: {},
  statusColorMap: {
    public: '#10b981',
  },
  md: null,
};
export default ReportList;
